import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_URL || (
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:3000'
    : 'https://grossistify-api.vercel.app'
);

// Types for company data from onboarding
export interface Warehouse {
  id: string;
  name: string;
  address: string;
  parkingAddress?: string | null;
  latitude?: number;
  longitude?: number;
}

export interface Customer {
  id: string;
  name: string;
  address: string;
  streetAddress?: string;
  postalCode?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  contactPerson?: string;
  phone?: string;
}

export interface CompanyInfo {
  id: string;
  name: string;
  orgNumber?: string;
  address?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
}

interface CompanyDataContextType {
  companyInfo: CompanyInfo | null;
  warehouses: Warehouse[];
  customers: Customer[];
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  addCustomer: (customer: Omit<Customer, 'id'>) => Promise<Customer | null>;
  updateCustomer: (id: string, data: Partial<Customer>) => Promise<boolean>;
  deleteCustomer: (id: string) => Promise<boolean>;
  addWarehouse: (warehouse: Omit<Warehouse, 'id'>) => Promise<Warehouse | null>;
}

const CompanyDataContext = createContext<CompanyDataContextType | undefined>(undefined);

export const useCompanyData = () => {
  const context = useContext(CompanyDataContext);
  if (!context) {
    throw new Error('useCompanyData must be used within a CompanyDataProvider');
  }
  return context;
};

interface CompanyDataProviderProps {
  children: ReactNode;
}

export const CompanyDataProvider: React.FC<CompanyDataProviderProps> = ({ children }) => {
  const { token, grossist } = useAuth();
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCompanyData = useCallback(async () => {
    if (!token || !grossist) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch company info
      const companyRes = await fetch(`${API_BASE_URL}/api/company`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (companyRes.ok) {
        const companyData = await companyRes.json();
        if (companyData.success) {
          setCompanyInfo(companyData.company);
        }
      }

      // Fetch warehouses
      const warehousesRes = await fetch(`${API_BASE_URL}/api/warehouses`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (warehousesRes.ok) {
        const warehousesData = await warehousesRes.json();
        if (warehousesData.success) {
          setWarehouses(warehousesData.warehouses || []);
        }
      }

      // Fetch customers
      const customersRes = await fetch(`${API_BASE_URL}/api/customers`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (customersRes.ok) {
        const customersData = await customersRes.json();
        if (customersData.success) {
          setCustomers(customersData.customers || []);
        }
      }
    } catch (err) {
      console.error('Error fetching company data:', err);
      setError('Kunde inte hämta företagsdata');
    } finally {
      setIsLoading(false);
    }
  }, [token, grossist]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  const addCustomer = async (customer: Omit<Customer, 'id'>): Promise<Customer | null> => {
    if (!token) return null;

    try {
      const response = await fetch(`${API_BASE_URL}/api/customers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(customer),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.customer) {
          setCustomers(prev => [...prev, data.customer]);
          return data.customer;
        }
      }
      return null;
    } catch (err) {
      console.error('Error adding customer:', err);
      return null;
    }
  };

  const updateCustomer = async (id: string, data: Partial<Customer>): Promise<boolean> => {
    if (!token) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCustomers(prev => prev.map(c => c.id === id ? { ...c, ...data } : c));
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error('Error updating customer:', err);
      return false;
    }
  };

  const deleteCustomer = async (id: string): Promise<boolean> => {
    if (!token) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        setCustomers(prev => prev.filter(c => c.id !== id));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error deleting customer:', err);
      return false;
    }
  };

  const addWarehouse = async (warehouse: Omit<Warehouse, 'id'>): Promise<Warehouse | null> => {
    if (!token) return null;

    try {
      const response = await fetch(`${API_BASE_URL}/api/warehouses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(warehouse),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.warehouse) {
          setWarehouses(prev => [...prev, data.warehouse]);
          return data.warehouse;
        }
      }
      return null;
    } catch (err) {
      console.error('Error adding warehouse:', err);
      return null;
    }
  };

  return (
    <CompanyDataContext.Provider
      value={{
        companyInfo,
        warehouses,
        customers,
        isLoading,
        error,
        refreshData: fetchCompanyData,
        addCustomer,
        updateCustomer,
        deleteCustomer,
        addWarehouse,
      }}
    >
      {children}
    </CompanyDataContext.Provider>
  );
};
